import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPriceHistory } from '@/api/clarityLens'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import {
  PriceHistoryMonthlyModel,
  PriceHistoryResponseModel,
} from '@/types/clarityLens'

type LineChartDataModel = {
  data: PriceHistoryMonthlyModel
}

export default function usePriceHistory(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string,
  metric: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()) &&
      Boolean(metric?.trim()),
    [startDate, endDate, branches, itemName, metric]
  )
  const loading = !isReady || isFetching

  const [seasonalityChartData, setSeasonalityChartData] =
    useState<LineChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      // Determine if metric should be passed to API
      const shouldPassMetric = metric !== 'ptiMoneyImpact'

      const response: PriceHistoryResponseModel = await getPriceHistory(
        branches,
        startDate,
        endDate,
        itemName,
        shouldPassMetric ? metric : undefined
      )

      console.log('response', response)

      // Convert seasonality to line chart
      const lineChart: LineChartDataModel = convertToLineChart(
        response.avg_material_code_price
      )
      setSeasonalityChartData(lineChart)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName, metric])

  const refetch = useCallback(() => {
    console.log('Refetching price history data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    seasonalityChartData,
    loading,
    refetch,
  }
}

function convertToLineChart(
  priceHistory: PriceHistoryMonthlyModel
): LineChartDataModel {
  return {
    data: priceHistory,
  }
}
